import type { ManualSection } from '../manual.types';

export const dashboardSection: ManualSection = {
  slug: 'dashboard',
  title: 'Dashboard',
  icon: 'ki-chart-pie-3',
  summary:
    'Qué mide cada tarjeta, de dónde sale el número y por qué los porcentajes no suman lo que esperarías.',
  audience: 'QA, operación y supervisión.',
  topics: [
    {
      id: 'de-donde-salen-los-datos',
      title: 'De dónde salen los datos',
      blocks: [
        {
          kind: 'p',
          text: 'El dashboard tiene **dos mitades que no comparten fuente**. La de arriba es tiempo real y vive en la memoria del backend; la de abajo (Histórico) es una consulta a la base analítica. Es normal que no cuadren al dígito: miden ventanas distintas y una de las dos se borra si el backend se reinicia.'
        },
        {
          kind: 'h4',
          text: 'El camino de una llamada hasta el dashboard'
        },
        {
          kind: 'steps',
          items: [
            'El **FreeSWITCH** origina la llamada y, mientras suena (early media), corre la detección CPD. Cuando decide algo, dispara el evento `CPD::RESULT`.',
            'El backend está suscrito al **ESL** del FS y normaliza cada evento a un formato común (`CallEventEnvelope`).',
            'Ese evento se publica en **Kafka**, en el topic `cpd.call.events`, usando el `callUuid` como llave — así todos los eventos de una misma llamada quedan ordenados.',
            'Dos consumidores lo leen en paralelo: el **agregador en memoria** (que alimenta la mitad de arriba, por WebSocket) y el **consumidor de CDR** (que escribe en **ClickHouse** y alimenta el Histórico y `/reports`).'
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Por qué importa que sean dos caminos',
          text: 'Si el dashboard en vivo se mueve pero el Histórico no, el problema está del lado de ClickHouse. Si pasa al revés, del lado del WebSocket o del agregador. Es lo primero que conviene mirar al reportar una diferencia.'
        },
        {
          kind: 'h4',
          text: 'Los cuatro eventos que se cuentan'
        },
        {
          kind: 'table',
          head: ['Evento', 'Cuándo se emite', 'Qué alimenta'],
          rows: [
            [
              '`channel_create`',
              'El FS crea el canal, o sea inicia el intento de llamada.',
              '**Nuevas** y **Llamadas/s**'
            ],
            ['`channel_answer`', 'El carrier contestó (200 OK).', '**Contestadas**'],
            [
              '`channel_hangup`',
              'La llamada terminó. Trae la causa de colgado, el `billsec` y la marca de bypass.',
              '**Colgadas**, **Bypass** y **Causas de colgado**'
            ],
            [
              '`cpd_result`',
              'La detección CPD emitió su veredicto. **Es la única fuente del veredicto.**',
              '**Buzón**, **Humano**, **Sin veredicto** y el top por operador'
            ]
          ]
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'El veredicto se cuenta una sola vez, a propósito',
          text: 'Una llamada con buzón detectado emite **dos** eventos que traen el veredicto: primero el `cpd_result` y después el `channel_hangup` con el veredicto propagado. El agregador cuenta **solo el `cpd_result`**; si contara los dos, todo saldría al doble. Efecto secundario a tener en mente: si el script `cpd_result.lua` no llegara a correr en un nodo, ese nodo sub-contaría veredictos y lo verías como una divergencia entre las causas de colgado y las tasas de CPD.'
        }
      ]
    },
    {
      id: 'los-cuatro-kpis',
      title: 'Las cuatro tarjetas de arriba',
      blocks: [
        {
          kind: 'kv',
          items: [
            {
              k: 'Sesiones activas',
              v: 'Llamadas vivas en este momento. Se calcula sumando 1 por cada `channel_create` y restando 1 por cada `channel_hangup`, de forma acumulada. **Es una estimación**, ver las advertencias más abajo.'
            },
            {
              k: 'Llamadas/s',
              v: 'Promedio de llamadas nuevas por segundo en la **ventana de 60 segundos**. Se muestra con dos decimales.'
            },
            {
              k: '% Buzón',
              v: 'Porcentaje de buzón sobre las llamadas **clasificadas** en la **ventana de 5 minutos**.'
            },
            {
              k: '% Humano',
              v: 'El complemento del anterior, sobre la misma ventana y el mismo denominador.'
            }
          ]
        },
        {
          kind: 'formula',
          label: 'Llamadas por segundo',
          expr: 'llamadas/s = channel_create en la ventana ÷ segundos de la ventana',
          example:
            'En la tarjeta se usa la ventana de 60 s. Si hubo 1 800 `channel_create` en los últimos 60 segundos, la tarjeta muestra `30.00`.',
          note: 'Redondeo a 2 decimales. En la tabla de ventanas verás el mismo cálculo para 10 s, 60 s y 5 min, y por eso los tres valores casi nunca coinciden: la de 10 s reacciona al instante y la de 5 min alisa los picos.'
        },
        {
          kind: 'formula',
          label: '% Buzón y % Humano',
          expr: '% Buzón  = buzón ÷ (buzón + humano)\n% Humano = humano ÷ (buzón + humano)',
          example:
            'Con 300 buzón, 200 humano y 50 sin veredicto en la ventana: % Buzón = 300 ÷ 500 = **60.0 %**, % Humano = 200 ÷ 500 = **40.0 %**. Las 50 sin veredicto **no** entran en la cuenta.',
          note: 'Los dos porcentajes siempre suman 100 %, porque comparten denominador.'
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'El denominador NO es el total de llamadas',
          text: 'Es el error más común al validar estos números. El denominador son las llamadas **clasificadas** = buzón + humano. Quedan fuera: las **sin veredicto** (`unknown`, el CPD corrió pero no concluyó) y las de **bypass** (el CPD ni siquiera se aplicó). Si divides buzón entre el total de llamadas te va a dar más bajo y **no** es que el dashboard esté mal.'
        }
      ]
    },
    {
      id: 'ventanas',
      title: 'Las ventanas de 10 s, 60 s y 5 min',
      blocks: [
        {
          kind: 'p',
          text: 'El backend guarda los conteos en cubetas de **un segundo** y arma cada ventana sumando las últimas N cubetas. Son ventanas **móviles**: no son "los últimos 5 minutos cerrados" sino los 300 segundos que terminan justo ahora, y se recalculan en cada tick.'
        },
        {
          kind: 'p',
          text: 'El servidor manda un snapshot completo **cada segundo**. No hay deltas: cada tick trae el panorama entero, así que si se pierde un mensaje el siguiente ya viene corregido.'
        },
        {
          kind: 'table',
          head: ['Columna de la tabla', 'Qué cuenta en esa ventana'],
          rows: [
            ['Nuevas', 'Eventos `channel_create`.'],
            ['Contestadas', 'Eventos `channel_answer`.'],
            ['Colgadas', 'Eventos `channel_hangup`.'],
            ['Buzón / Humano / Sin veredicto', 'Eventos `cpd_result` con cada veredicto.'],
            [
              'Bypass',
              'Llamadas que terminaron con la marca de bypass, o sea que pasaron **sin** aplicarles CPD.'
            ],
            ['Llamadas/s', 'Nuevas ÷ segundos de la ventana.'],
            ['% Buzón', 'Con la fórmula de arriba, dentro de esa misma ventana.']
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Nuevas y Colgadas no tienen por qué coincidir',
          text: 'Una llamada nace en una ventana y muere en otra. En tráfico estable los dos números se parecen; cuando el Blaster arranca o para una campaña se separan mucho, y eso es correcto.'
        }
      ]
    },
    {
      id: 'bypass',
      title: 'Bypass: cuándo aparece y por qué no cuenta',
      blocks: [
        {
          kind: 'p',
          text: 'Una llamada de **bypass** es una que la política CPD decidió dejar pasar sin analizar — típicamente una ruta sin calibrar, o el grupo `onering` (routeGroupId 99). No hay veredicto porque nunca hubo detección.'
        },
        {
          kind: 'ul',
          items: [
            'El contador de **Bypass** se alimenta del `channel_hangup` que trae la marca, no del `cpd_result`.',
            'Estas llamadas **no** entran en % Buzón ni en % Humano, ni en el top por operador.',
            'Sirven sobre todo para **validar configuración**: si una ruta que debería tener CPD aparece con volumen alto de bypass, la política está mal armada. Al revés, esperar mucho bypass en `onering` es lo normal.'
          ]
        }
      ]
    },
    {
      id: 'top-operador-causa',
      title: 'Top por operador y causas de colgado',
      blocks: [
        {
          kind: 'p',
          text: 'Las dos tablas de abajo del bloque en vivo son siempre sobre los **últimos 5 minutos** y muestran como máximo **10 renglones**, sin importar la ventana que estés viendo arriba.'
        },
        {
          kind: 'kv',
          items: [
            {
              k: 'Top por operador',
              v: 'Cuenta veredictos por operador (buzón / humano / sin veredicto) y ordena por la **suma de los tres**. Solo aparecen operadores que el Blaster mandó en el header `X-Operator-Id`; si viene vacío, la llamada no entra en esta tabla.'
            },
            {
              k: 'Causas de colgado',
              v: 'Cuenta el `Hangup-Cause` de cada `channel_hangup`, ordenado de mayor a menor. `CALL_REJECTED` es la causa que deja el CPD al cortar un buzón con SIP 603.'
            }
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Por qué el top por operador puede sumar menos que el total',
          text: 'Solo cuenta llamadas con veredicto **y** con operador identificado, y corta en 10. Si hay más de 10 operadores con tráfico, los de la cola no se ven.'
        }
      ]
    },
    {
      id: 'limitaciones-tiempo-real',
      title: 'Limitaciones del bloque en tiempo real (léelo antes de reportar un bug)',
      blocks: [
        {
          kind: 'note',
          tone: 'warn',
          title: 'Todo esto vive en la memoria del backend',
          text: 'Los contadores del bloque en vivo **no se persisten**. Si el backend se reinicia o se redespliega, las ventanas arrancan en cero y las Sesiones activas también. No es pérdida de datos: el histórico y `/reports` siguen completos porque esos salen de ClickHouse.'
        },
        {
          kind: 'ul',
          items: [
            '**Sesiones activas es una estimación.** Si un `channel_hangup` se pierde, el contador se queda alto de más. Nunca baja de cero, así que después de un reinicio arranca sub-contando y se va corrigiendo solo conforme entra tráfico nuevo. Para un número exacto de canales, la fuente es el FS (`show channels count`), no el dashboard.',
            '**El agregador es de una sola instancia.** Cada réplica del backend agrega únicamente lo que ella ve. Hoy corre en una instancia, así que no hay problema; si algún día hay varias, este bloque necesitaría un almacén compartido.',
            '**La ventana máxima es de 5 minutos.** No hay forma de pedir 15 minutos en el bloque en vivo; para rangos más largos está el Histórico.',
            '**El log de eventos guarda los últimos 200.** Cuando abres el dashboard te "pones al día" con ese buffer; lo anterior solo existe en `/reports`.'
          ]
        }
      ]
    },
    {
      id: 'historico',
      title: 'La sección Histórico',
      blocks: [
        {
          kind: 'p',
          text: 'El bloque de abajo consulta `GET /v1/reports/dashboard` con el rango de fechas que elijas y trae cuatro cosas: el **resumen**, el **top de operadores**, el **top de rutas** y las **causas de colgado**. Sale de ClickHouse, así que sobrevive a los reinicios del backend.'
        },
        {
          kind: 'kv',
          items: [
            { k: 'Total', v: 'Llamadas iniciadas: eventos `channel_create` en el rango.' },
            { k: 'Contestadas', v: 'Eventos `channel_answer`.' },
            {
              k: 'Humano / Buzón',
              v: 'Eventos `cpd_result` con ese veredicto, y al lado su porcentaje con la **misma fórmula** que arriba (sobre buzón + humano).'
            },
            {
              k: 'Sin veredicto',
              v: 'Eventos `cpd_result` con veredicto `unknown`.'
            },
            {
              k: 'Minutos facturables',
              v: 'Suma de `billsec` de todos los `channel_hangup` del rango, mostrada en minutos u horas.'
            }
          ]
        },
        {
          kind: 'p',
          text: 'Las columnas **% Buzón** de las tablas por operador y por ruta se calculan en el navegador con esos mismos criterios: buzón ÷ (buzón + humano) de ese renglón.'
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Los días y las horas son UTC, no hora de México',
          text: 'La base guarda los tiempos en UTC y agrupa por día y hora UTC. México está en UTC−6, así que un reporte de "hoy" en realidad cubre de las **18:00 de ayer a las 17:59 de hoy**, hora local. Es lo que hay que tener en mente al cruzar contra cualquier otra fuente. Está documentado con más detalle en [Reportes → Zona horaria](/manual/reportes#zona-horaria).'
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'El contador de Bypass del resumen puede salir en 0 falso',
          text: 'La tabla pre-agregada por día no guarda la columna de bypass, así que el resumen reporta 0 salvo que filtres operador **y** ruta al mismo tiempo. Un 0 ahí significa "no lo sé", no "no hubo". El dato correcto está en el bloque en vivo y en el reporte **por hora**.'
        }
      ]
    }
  ]
};
